<template>
  <div class="space-y-6">
    <div class="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Mensajes Programados</h1>
          <p class="text-indigo-100 mt-1">Envío de notificaciones push programadas</p>
        </div>
        <div class="hidden sm:flex space-x-4">
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.total }}</p>
            <p class="text-xs text-indigo-100">Total</p>
          </div>
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.pending }}</p>
            <p class="text-xs text-indigo-100">Pendientes</p>
          </div>
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.sent }}</p>
            <p class="text-xs text-indigo-100">Enviados</p>
          </div>
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.failed }}</p>
            <p class="text-xs text-indigo-100">Fallidos</p>
          </div>
        </div>
      </div>
    </div>

    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Estado</label>
          <select
            v-model="statusFilter"
            @change="onFilterChange"
            class="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="sending">Enviando</option>
            <option value="sent">Enviado</option>
            <option value="failed">Fallido</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
        <div class="ml-auto">
          <button @click="router.push('/scheduled-messages/create')" class="btn btn-primary flex items-center gap-2">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Mensaje
          </button>
        </div>
      </div>
    </div>

    <div class="card">
      <DataTable
        :value="messages"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :totalRecords="pagination.total"
        :lazy="true"
        @page="onPage"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Mensajes</h2>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay mensajes programados</h3>
            <p class="mt-1 text-sm text-gray-500">Crea un nuevo mensaje programado para enviar notificaciones push.</p>
          </div>
        </template>

        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p class="mt-2 text-gray-600">Cargando mensajes...</p>
          </div>
        </template>

        <Column field="title" header="Título">
          <template #body="{ data }">
            <div>
              <p class="text-sm font-medium text-gray-900">{{ data.title }}</p>
              <p class="text-xs text-gray-500 truncate max-w-xs">{{ data.body }}</p>
            </div>
          </template>
        </Column>

        <Column header="Imagen">
          <template #body="{ data }">
            <img v-if="data.imageUrl" :src="data.imageUrl" class="h-10 w-10 rounded object-cover" />
            <span v-else class="text-xs text-gray-400">Sin imagen</span>
          </template>
        </Column>

        <Column header="Destino">
          <template #body="{ data }">
            <div class="flex flex-col gap-1">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="targetClass(data.target)">
                {{ targetLabel(data.target) }}
              </span>
              <span v-if="data.animalType" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {{ data.animalType.name }}
              </span>
            </div>
          </template>
        </Column>

        <Column field="scheduledAt" header="Fecha Programada" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{ formatDate(data.scheduledAt) }}</span>
          </template>
        </Column>

        <Column header="Estado">
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="statusClass(data.status)">
              {{ statusLabel(data.status) }}
            </span>
          </template>
        </Column>

        <Column header="Resultados">
          <template #body="{ data }">
            <div v-if="data.status === 'sent'" class="text-xs">
              <span class="text-green-600">{{ data.sentCount }} enviados</span>
              <span v-if="data.failedCount > 0" class="text-red-600 ml-1">/ {{ data.failedCount }} fallidos</span>
            </div>
            <span v-else class="text-xs text-gray-400">-</span>
          </template>
        </Column>

        <Column header="Acciones">
          <template #body="{ data }">
            <div class="flex items-center gap-1">
              <button
                v-if="data.status === 'pending'"
                @click="openEditModal(data)"
                class="inline-flex items-center p-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all duration-200"
                title="Editar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                v-if="data.status === 'pending'"
                @click="confirmSendNow(data)"
                class="inline-flex items-center p-2 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-all duration-200"
                title="Enviar Ahora"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
              <button
                v-if="data.status === 'pending'"
                @click="confirmCancel(data)"
                class="inline-flex items-center p-2 text-sm font-medium text-yellow-600 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-all duration-200"
                title="Cancelar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </button>
              <button
                v-if="data.status !== 'sending'"
                @click="confirmDelete(data)"
                class="inline-flex items-center p-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-all duration-200"
                title="Eliminar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showFormModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showFormModal = false"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div class="bg-white px-6 pt-6 pb-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              {{ editingMessage ? 'Editar Mensaje Programado' : 'Nuevo Mensaje Programado' }}
            </h3>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input v-model="form.title" type="text" class="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Título de la notificación" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Mensaje *</label>
                <textarea v-model="form.body" rows="3" class="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Contenido de la notificación"></textarea>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Imagen</label>
                <input
                  type="file"
                  accept="image/*"
                  @change="handleImageFileChange"
                  class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                <img v-if="imagePreview" :src="imagePreview" class="mt-2 h-20 rounded object-cover" />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de animal</label>
                  <select v-model="form.animalTypeId" class="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option :value="null">Todos los animales</option>
                    <option v-for="type in animalTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">URL del Enlace</label>
                  <input v-model="form.linkUrl" type="text" class="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="mascotijo://ruta o https://..." />
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Fecha y Hora *</label>
                  <input v-model="form.scheduledAt" type="datetime-local" class="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Destino</label>
                  <select v-model="form.target" class="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="all">Todos</option>
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex justify-end gap-3">
            <button @click="showFormModal = false" class="btn btn-secondary">Cancelar</button>
            <button @click="saveMessage" :disabled="saving" class="btn btn-primary">
              {{ saving ? 'Guardando...' : (editingMessage ? 'Actualizar' : 'Crear') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Action Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showConfirmModal = false"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10" :class="confirmAction === 'delete' ? 'bg-red-100' : confirmAction === 'cancel' ? 'bg-yellow-100' : 'bg-green-100'">
                <svg class="h-6 w-6" :class="confirmAction === 'delete' ? 'text-red-600' : confirmAction === 'cancel' ? 'text-yellow-600' : 'text-green-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">{{ confirmTitle }}</h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">{{ confirmText }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button @click="executeConfirmAction" :disabled="actionLoading" class="w-full sm:w-auto sm:ml-3" :class="confirmAction === 'delete' ? 'btn btn-danger' : confirmAction === 'cancel' ? 'btn btn-warning' : 'btn btn-primary'">
              {{ actionLoading ? 'Procesando...' : confirmButtonLabel }}
            </button>
            <button @click="showConfirmModal = false" class="btn btn-secondary w-full sm:w-auto mt-3 sm:mt-0">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import scheduledMessageService from '@/services/scheduledMessageService'
import { animalTypeService } from '@/services/animalTypeService'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

export default {
  name: 'ScheduledMessages',
  components: { DataTable, Column },
  setup() {
    const toast = useToast()
    const router = useRouter()
    const loading = ref(false)
    const saving = ref(false)
    const actionLoading = ref(false)
    const messages = ref([])
    const animalTypes = ref([])
    const imageFile = ref(null)
    const imagePreview = ref('')
    const statusFilter = ref('')
    const showFormModal = ref(false)
    const showConfirmModal = ref(false)
    const editingMessage = ref(null)
    const confirmAction = ref('')
    const confirmTitle = ref('')
    const confirmText = ref('')
    const confirmButtonLabel = ref('')
    const selectedMessage = ref(null)
    const stats = reactive({ total: 0, pending: 0, sent: 0, failed: 0 })
    const pagination = reactive({ page: 1, limit: 10, total: 0, totalPages: 0 })

    const form = reactive({
      title: '',
      body: '',
      imageUrl: '',
      linkUrl: '',
      linkLabel: '',
      scheduledAt: '',
      target: 'all',
      animalTypeId: null
    })

    const resetForm = () => {
      form.title = ''
      form.body = ''
      form.imageUrl = ''
      form.linkUrl = ''
      form.linkLabel = ''
      form.scheduledAt = ''
      form.target = 'all'
      form.animalTypeId = null
      imageFile.value = null
      imagePreview.value = ''
    }

    const loadMessages = async () => {
      loading.value = true
      try {
        const response = await scheduledMessageService.getScheduledMessages({
          page: pagination.page,
          limit: pagination.limit,
          status: statusFilter.value
        })
        messages.value = response.data.data
        Object.assign(pagination, response.data.pagination)
      } catch {
        toast.error('Error al cargar mensajes programados')
      } finally {
        loading.value = false
      }
    }

    const loadStats = async () => {
      try {
        const response = await scheduledMessageService.getStats()
        Object.assign(stats, response.data)
      } catch {}
    }

    const onFilterChange = () => {
      pagination.page = 1
      loadMessages()
    }

    const onPage = (event) => {
      pagination.page = event.page + 1
      pagination.limit = event.rows
      loadMessages()
    }

    const openCreateModal = () => {
      editingMessage.value = null
      resetForm()
      showFormModal.value = true
    }

    const openEditModal = (message) => {
      editingMessage.value = message
      form.title = message.title
      form.body = message.body
      form.imageUrl = message.imageUrl || ''
      form.linkUrl = message.linkUrl || ''
      form.linkLabel = message.linkLabel || ''
      form.scheduledAt = new Date(message.scheduledAt).toISOString().slice(0, 16)
      form.target = message.target
      form.animalTypeId = message.animalTypeId || null
      imageFile.value = null
      imagePreview.value = message.imageUrl || ''
      showFormModal.value = true
    }

    const handleImageFileChange = (event) => {
      const file = event.target.files?.[0]
      if (file) {
        imageFile.value = file
        imagePreview.value = URL.createObjectURL(file)
      }
    }

    const saveMessage = async () => {
      if (!form.title || !form.body || !form.scheduledAt) {
        toast.error('Título, mensaje y fecha son obligatorios')
        return
      }

      saving.value = true
      try {
        let data
        if (imageFile.value) {
          data = new FormData()
          data.append('title', form.title)
          data.append('body', form.body)
          data.append('image', imageFile.value)
          data.append('scheduledAt', new Date(form.scheduledAt).toISOString())
          data.append('target', form.target)
          if (form.linkUrl) data.append('linkUrl', form.linkUrl)
          if (form.linkLabel) data.append('linkLabel', form.linkLabel)
          if (form.animalTypeId) data.append('animalTypeId', String(form.animalTypeId))
        } else {
          data = {
            title: form.title,
            body: form.body,
            imageUrl: form.imageUrl || null,
            linkUrl: form.linkUrl || null,
            linkLabel: form.linkLabel || null,
            scheduledAt: new Date(form.scheduledAt).toISOString(),
            target: form.target,
            animalTypeId: form.animalTypeId || null
          }
        }

        if (editingMessage.value) {
          await scheduledMessageService.update(editingMessage.value.id, data)
          toast.success('Mensaje actualizado correctamente')
        } else {
          await scheduledMessageService.create(data)
          toast.success('Mensaje programado creado correctamente')
        }

        showFormModal.value = false
        loadMessages()
        loadStats()
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Error al guardar mensaje')
      } finally {
        saving.value = false
      }
    }

    const confirmSendNow = (message) => {
      selectedMessage.value = message
      confirmAction.value = 'sendNow'
      confirmTitle.value = 'Enviar Ahora'
      confirmText.value = `¿Estás seguro de que quieres enviar "${message.title}" inmediatamente?`
      confirmButtonLabel.value = 'Enviar Ahora'
      showConfirmModal.value = true
    }

    const confirmCancel = (message) => {
      selectedMessage.value = message
      confirmAction.value = 'cancel'
      confirmTitle.value = 'Cancelar Mensaje'
      confirmText.value = `¿Estás seguro de que quieres cancelar el envío de "${message.title}"?`
      confirmButtonLabel.value = 'Cancelar Mensaje'
      showConfirmModal.value = true
    }

    const confirmDelete = (message) => {
      selectedMessage.value = message
      confirmAction.value = 'delete'
      confirmTitle.value = 'Eliminar Mensaje'
      confirmText.value = `¿Estás seguro de que quieres eliminar "${message.title}"? Esta acción no se puede deshacer.`
      confirmButtonLabel.value = 'Eliminar'
      showConfirmModal.value = true
    }

    const executeConfirmAction = async () => {
      actionLoading.value = true
      try {
        const id = selectedMessage.value.id
        if (confirmAction.value === 'sendNow') {
          await scheduledMessageService.sendNow(id)
          toast.success('Mensaje en cola para envío inmediato')
        } else if (confirmAction.value === 'cancel') {
          await scheduledMessageService.cancel(id)
          toast.success('Mensaje cancelado correctamente')
        } else if (confirmAction.value === 'delete') {
          await scheduledMessageService.delete(id)
          toast.success('Mensaje eliminado correctamente')
        }
        showConfirmModal.value = false
        loadMessages()
        loadStats()
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Error al ejecutar la acción')
      } finally {
        actionLoading.value = false
      }
    }

    const statusLabel = (status) => {
      const labels = { pending: 'Pendiente', sending: 'Enviando', sent: 'Enviado', failed: 'Fallido', cancelled: 'Cancelado' }
      return labels[status] || status
    }

    const statusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        sending: 'bg-blue-100 text-blue-800',
        sent: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
        cancelled: 'bg-gray-100 text-gray-800'
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }

    const targetLabel = (target) => {
      const labels = { all: 'Todos', android: 'Android', ios: 'iOS' }
      return labels[target] || target
    }

    const targetClass = (target) => {
      const classes = {
        all: 'bg-indigo-100 text-indigo-800',
        android: 'bg-green-100 text-green-800',
        ios: 'bg-blue-100 text-blue-800'
      }
      return classes[target] || 'bg-gray-100 text-gray-800'
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    }

    onMounted(() => {
      loadMessages()
      loadStats()
      animalTypeService.getAllAnimalTypes().then(res => {
        animalTypes.value = res.data || res
      }).catch(() => {})
    })

    return {
      loading, saving, actionLoading, messages, statusFilter, showFormModal, showConfirmModal,
      editingMessage, confirmAction, confirmTitle, confirmText, confirmButtonLabel,
      stats, pagination, form, animalTypes, imageFile, imagePreview,
      router,
      onFilterChange, onPage, openCreateModal, openEditModal, saveMessage,
      handleImageFileChange,
      confirmSendNow, confirmCancel, confirmDelete, executeConfirmAction,
      statusLabel, statusClass, targetLabel, targetClass, formatDate
    }
  }
}
</script>
